import { cloneElement, isValidElement, useId } from 'react';
import type { ReactNode } from 'react';

/** Labelled form field wrapper with hint + error slots. */
export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const autoId = useId();
  const hintId = useId();
  const errorId = useId();
  // Auto-associate the visible label with its control (WCAG 1.3.1 / 3.3.2).
  let control = children;
  let controlId = htmlFor;
  if (isValidElement(children)) {
    const props = children.props as { id?: string; 'aria-describedby'?: string };
    controlId = controlId ?? props.id ?? `field-${autoId}`;
    const describedBy = [
      props['aria-describedby'],
      hint ? `hint-${hintId}` : null,
      error ? `err-${errorId}` : null,
    ].filter(Boolean).join(' ') || undefined;
    control = cloneElement(children, {
      id: controlId,
      ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      ...(error ? { 'aria-invalid': true } : {}),
    } as Record<string, unknown>);
  }
  return (
    <div className="field">
      <label className="field-label" htmlFor={controlId}>
        {label}
        {required && (
          <span className="required-mark" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {control}
      {hint && <span className="field-hint" id={`hint-${hintId}`}>{hint}</span>}
      {error && (
        <span className="field-error" id={`err-${errorId}`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

interface ControlProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ invalid, ...props }: ControlProps) {
  return (
    <input
      className="input"
      aria-invalid={invalid ? 'true' : undefined}
      {...props}
    />
  );
}

export function Textarea({
  invalid,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      className="textarea"
      aria-invalid={invalid ? 'true' : undefined}
      {...props}
    />
  );
}

export function Select({
  invalid,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      className="select"
      aria-invalid={invalid ? 'true' : undefined}
      {...props}
    >
      {children}
    </select>
  );
}

/** Two-column row of fields (collapses on small screens). */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="form-row">{children}</div>;
}

/** Right-aligned submit/cancel actions. */
export function FormActions({ children }: { children: ReactNode }) {
  return <div className="form-actions">{children}</div>;
}
